import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface ZipPromotion {
  promotionId: string;
  title: string;
  description: string;
  promotionType: 'use_10_get_1_free';
  zipCodes: string[];
  servicesRequired: number;
  rewardType: 'free_service';
  startDate: string;
  endDate: string;
  isActive: boolean;
  applicableServiceTypes?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ZipPromotionListResponse {
  zipPromotions: ZipPromotion[];
  lastEvaluatedKey?: Record<string, unknown>;
}

class ZipPromotionManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: { 'Content-Type': 'application/json' },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async listZipPromotions(params?: { limit?: number; lastEvaluatedKey?: Record<string, unknown> }): Promise<ZipPromotionListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) {
      queryParams.append('lastEvaluatedKey', encodeURIComponent(JSON.stringify(params.lastEvaluatedKey)));
    }
    const response = await this.api.get<{ data: ZipPromotionListResponse }>(
      `/admin/zip-promotions?${queryParams.toString()}`
    );
    return response.data.data;
  }

  async createZipPromotion(promotion: Omit<ZipPromotion, 'promotionId' | 'createdAt' | 'updatedAt'>): Promise<ZipPromotion> {
    const response = await this.api.post<{ data: { zipPromotion: ZipPromotion } }>('/admin/zip-promotions', promotion);
    return response.data.data.zipPromotion;
  }

  async updateZipPromotion(promotionId: string, updates: Partial<ZipPromotion>): Promise<ZipPromotion> {
    const response = await this.api.put<{ data: { zipPromotion: ZipPromotion } }>(
      `/admin/zip-promotions/${promotionId}`,
      updates
    );
    return response.data.data.zipPromotion;
  }

  async deleteZipPromotion(promotionId: string): Promise<void> {
    await this.api.delete(`/admin/zip-promotions/${promotionId}`);
  }
}

export const zipPromotionManagementService = new ZipPromotionManagementService();
